import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { env } from "../lib/env.js";
import { ApiError, createSubmission, releaseSubmission } from "../lib/api.js";
import { clearIdempotencyKey, submissionIdempotencyKey } from "../lib/idempotency.js";
import { useSpark } from "../hooks/useSpark.js";
import { useBackgroundDim } from "../background/useBackgroundDim.js";
import { APPEAR_DURATION_MS } from "../lib/spark.js";
import { HoldButton } from "../components/HoldButton.js";
import { ScreenCopy } from "../components/ScreenCopy.js";
import { ChargeCanvas } from "../components/ChargeCanvas.js";
import { SparkCanvas } from "../components/SparkCanvas.js";
import styles from "./Launch.module.css";

/** Empty page, only the universe, before the welcome copy fades in. */
const INTRO_DELAY_MS = 1000;
/** From the welcome copy to the spark fading in. */
const SPARK_DELAY_MS = 3500;
/** From the spark to the welcome copy fading out. */
const HOLD_DELAY_MS = 2000;
/** Must match the `.leaving` fade-out in ScreenCopy.module.css. */
const COPY_FADE_OUT_MS = 1000;

type Phase = "empty" | "welcome" | "welcomeOut" | "hold";
type HoldCopy = "charge" | "release";

interface Destination {
  path: "/done";
  state: Record<string, unknown>;
}

/**
 * Stores the submission and releases it in one go. Outcomes the visitor can't act on
 * become a destination; anything else is thrown for the screen to show.
 */
async function sendParticle(): Promise<Destination> {
  // There is a single round: once it has closed, the spark can't join any more.
  const roundClosed: Destination = { path: "/done", state: { roundClosed: true } };
  try {
    const created = await createSubmission({}, submissionIdempotencyKey());
    if (created.autoReleased && created.particle) {
      return { path: "/done", state: { justSent: true } };
    }
    if (!created.canRelease) return roundClosed;

    await releaseSubmission(created.submissionId);
    clearIdempotencyKey();
    return { path: "/done", state: { justSent: true } };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.code === "ROUND_CLOSED") return roundClosed;
      // A device that already took part is sent to the end state rather than shown
      // an error it cannot act on.
      if (err.code === "ALREADY_SUBMITTED" || err.code === "ALREADY_RELEASED") {
        return { path: "/done", state: {} };
      }
    }
    throw err;
  }
}

export function Launch() {
  const navigate = useNavigate();
  const spark = useSpark();
  const dimBackground = useBackgroundDim();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [phase, setPhase] = useState<Phase>("empty");
  // Once the spark has faded in, ahead of the call to action.
  const [buttonShown, setButtonShown] = useState(false);
  const [charged, setCharged] = useState(false);
  const [holdCopy, setHoldCopy] = useState<HoldCopy>("charge");
  // The shown copy lags the charge by a fade-out, so it can leave before it is swapped.
  const wantedHoldCopy: HoldCopy = charged ? "release" : "charge";

  useEffect(() => spark.onFrame((state) => setCharged(state.charge >= 1)), [spark]);

  useEffect(() => {
    if (wantedHoldCopy === holdCopy) return;
    const timer = setTimeout(() => setHoldCopy(wantedHoldCopy), COPY_FADE_OUT_MS);
    return () => clearTimeout(timer);
  }, [wantedHoldCopy, holdCopy]);

  useEffect(() => {
    const steps: [number, () => void][] = [
      [
        INTRO_DELAY_MS,
        () => {
          setPhase("welcome");
          // Darkens until the spark has fully faded in.
          dimBackground(SPARK_DELAY_MS + APPEAR_DURATION_MS);
        },
      ],
      [SPARK_DELAY_MS, () => spark.appear()],
      [APPEAR_DURATION_MS, () => setButtonShown(true)],
      [HOLD_DELAY_MS - APPEAR_DURATION_MS, () => setPhase("welcomeOut")],
      [COPY_FADE_OUT_MS, () => setPhase("hold")],
    ];
    let at = 0;
    const timers = steps.map(([delay, step]) => setTimeout(step, (at += delay)));
    return () => timers.forEach(clearTimeout);
  }, [spark, dimBackground]);

  async function onLaunch(): Promise<void> {
    setBusy(true);
    setError(null);
    const flight = spark.launch();
    if (env.tweak) {
      await flight;
      spark.reset();
      setBusy(false);
      return;
    }
    try {
      const [destination] = await Promise.all([sendParticle(), flight]);
      navigate(destination.path, { state: destination.state, replace: true });
    } catch (err) {
      await flight;
      setError(err instanceof ApiError ? err.message : "Something went wrong. Please try again.");
      spark.reset();
      setBusy(false);
    }
  }

  const launchRef = useRef(onLaunch);
  useEffect(() => {
    launchRef.current = onLaunch;
  });

  useEffect(() => {
    if (!env.tweak) return;
    let unmount: (() => void) | undefined;
    let cancelled = false;
    void import("../debug/tweakPanel.js")
      .then(({ mountTweakPanel }) =>
        mountTweakPanel(() => {
          if (spark.state.flightStart === null) void launchRef.current();
        }),
      )
      .then((destroy) => {
        if (cancelled) destroy();
        else unmount = destroy;
      });
    return () => {
      cancelled = true;
      unmount?.();
    };
  }, [spark]);

  return (
    <>
      {/* Under the spark, so its halo sits where the stream arrives. */}
      <ChargeCanvas spark={spark} anchorRef={buttonRef} />
      <SparkCanvas spark={spark} />
      <div className={styles.screen}>
        {phase === "welcome" || phase === "welcomeOut" ? (
          <ScreenCopy
            key="welcome"
            headline="Every change begins with a single spark."
            subline="Today, each of us adds a spark. Together, they become something none of us could create alone."
            leaving={phase === "welcomeOut"}
          />
        ) : phase === "hold" ? (
          <ScreenCopy
            key={holdCopy}
            headline={
              holdCopy === "release"
                ? "Release your spark."
                : "Press and hold to charge your spark."
            }
            leaving={wantedHoldCopy !== holdCopy}
          />
        ) : null}
        {buttonShown ? (
          <div className={styles.actions}>
            {error ? (
              <p className={styles.error} role="alert">
                {error}
              </p>
            ) : null}
            <HoldButton
              buttonRef={buttonRef}
              spark={spark}
              onLaunch={() => void onLaunch()}
              disabled={busy}
              busyLabel="Sending…"
            />
          </div>
        ) : null}
      </div>
    </>
  );
}
